import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Spinner } from '@amzn/awsui-components-core';
import { AttendeePath, AttendeePathButton, ATTENDEE_PATH_OPTIONS, HUDDLE_CONTACTS, HUDDLE_SLACK_CHANNEL } from '../constants/EAHuddleConstants';
import { useUserMetrics } from 'src/components/fintech-ops/shared/context/UserMetricsProvider';
import { HuddleStatsService } from '../services/HuddleStatsService';
import { SLACK_DM_URL, SLACK_ICON_URL, generateProfilePictureUrl } from 'src/constants/AppConstants';
import huddleLogo from 'src/images/huddle-plus-logo.svg';
import styles from './AttendeePathPicker.module.scss';

// Stat icon assets
import iconUsers from '../assets/stat-users.svg';
import iconBuilding from '../assets/stat-leaders.svg';
import iconChart from '../assets/stat-sessions.svg';
import iconCalendarHeart from '../assets/stat-meetings.svg';

export type { AttendeePath };

// ══════════════════════════════════════════════════════════════════════════════
// ActionCards (inlined from components/ActionCards.tsx)
// ══════════════════════════════════════════════════════════════════════════════

interface ActionCardProps {
  path: AttendeePath;
  title: string;
  description: string;
  previewImage?: React.ReactNode;
  buttons: AttendeePathButton[];
  disabled?: boolean;
  shimmer?: boolean;
  isProcessing?: boolean;
  processingError?: string | null;
  onSelect: (path: AttendeePath) => void;
  onButtonClick?: (path: AttendeePath, buttonLabel: string) => void;
  onFileDrop?: (file: File) => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
  path,
  title,
  description,
  previewImage,
  buttons,
  disabled = false,
  shimmer = false,
  isProcessing = false,
  processingError,
  onSelect,
  onButtonClick,
  onFileDrop
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleCardClick = () => {
    if (disabled) return;
    const primaryLabel = buttons[0]?.label ?? '';
    if (onButtonClick) {
      onButtonClick(path, primaryLabel);
    } else {
      onSelect(path);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!onFileDrop) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    },
    [onFileDrop]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (!onFileDrop) return;
      const file = e.dataTransfer.files?.[0];
      if (file) onFileDrop(file);
    },
    [onFileDrop]
  );

  const cardClassName = [
    styles.actionCard,
    shimmer ? styles.actionShimmer : '',
    isDragOver ? styles.actionDragOver : '',
    disabled ? styles.actionCardDisabled : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cardClassName}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={`${title}. ${description}`}
      aria-disabled={disabled ? 'true' : undefined}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className={styles.actionPreview}>{previewImage ?? <div />}</div>
      <p className={styles.actionTitle}>{title}</p>
      <p className={styles.actionDescription}>{description}</p>
      {onFileDrop && (
        <div
          className={`${styles.actionDropZone} ${isDragOver ? styles.actionDropZoneActive : ''} ${
            isProcessing ? styles.actionDropZoneProcessing : ''
          } ${processingError ? styles.actionDropZoneError : ''}`}
        >
          {isProcessing ? (
            <>
              <Spinner size="normal" />
              <span className={styles.actionDropZoneText}>Processing file...</span>
            </>
          ) : processingError ? (
            <>
              <span className={styles.actionDropZoneTextError}>Failed to process file</span>
              <span className={styles.actionDropZoneHint}>Try again or use a different format</span>
            </>
          ) : (
            <>
              <span className={styles.actionDropZoneText}>{isDragOver ? 'Drop it!' : 'Drag & drop file here'}</span>
              <span className={styles.actionDropZoneHint}>.xlsx, .csv, .docx, .pdf</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── ActionCardsGrid ────────────────────────────────────────────────────────────

interface ActionCardsGridProps {
  onSelect: (path: AttendeePath) => void;
  onButtonClick?: (path: AttendeePath, buttonLabel: string) => void;
  disabledPaths?: Set<AttendeePath>;
  onFileDrop?: (file: File) => void;
  isProcessing?: boolean;
  processingError?: string | null;
}

const ActionCardsGrid: React.FC<ActionCardsGridProps> = ({ onSelect, onButtonClick, disabledPaths, onFileDrop, isProcessing, processingError }) => {
  // Reorder: clone (left), upload (center, primary), manual (right)
  const ordered = [
    ATTENDEE_PATH_OPTIONS.find((o) => o.path === 'clone')!,
    ATTENDEE_PATH_OPTIONS.find((o) => o.path === 'upload')!,
    ATTENDEE_PATH_OPTIONS.find((o) => o.path === 'manual')!
  ];

  return (
    <div className={styles.actionGrid} role="group" aria-label="Attendee path options">
      {ordered.map((option) => {
        const isPrimary = option.path === 'upload';
        return (
          <div key={option.path} className={isPrimary ? styles.actionPrimaryCard : undefined}>
            <ActionCard
              path={option.path}
              title={option.title}
              description={option.description}
              buttons={option.buttons}
              shimmer={isPrimary}
              isProcessing={isPrimary ? isProcessing : undefined}
              processingError={isPrimary ? processingError : undefined}
              previewImage={
                <img src={option.previewImage} alt={`${option.title} preview`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              }
              disabled={disabledPaths?.has(option.path)}
              onSelect={onSelect}
              onButtonClick={onButtonClick}
              onFileDrop={isPrimary ? onFileDrop : undefined}
            />
          </div>
        );
      })}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// StatsBar (inlined from components/StatsBar.tsx)
// ══════════════════════════════════════════════════════════════════════════════

// ── Exported Interfaces ────────────────────────────────────────────────────────

export interface StatItem {
  id: string;
  icon: React.ReactNode;
  value: string | null;
  label: string;
}

export interface HuddleStats {
  stats: StatItem[];
  isLoading: boolean;
}

// ── Internal Interfaces ────────────────────────────────────────────────────────

interface CachedMetrics {
  users: string;
  sessions: string;
  expiry: number;
}

// ── Constants ──────────────────────────────────────────────────────────────────

export const METRICS_CACHE_KEY = 'huddle_user_session_metrics';
export const METRICS_CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

/** Used to filter out the aggregate "All Applications" row from metrics data */
const ALL_APPLICATIONS = 'All Applications';

// ── Cache Helpers ──────────────────────────────────────────────────────────────

/**
 * Reads cached metrics from sessionStorage.
 * Returns null if cache is missing, expired, or on any error.
 */
export function getCachedMetrics(): CachedMetrics | null {
  try {
    const raw = sessionStorage.getItem(METRICS_CACHE_KEY);
    if (!raw) return null;
    const cached: CachedMetrics = JSON.parse(raw);
    return Date.now() < cached.expiry ? cached : null;
  } catch {
    return null;
  }
}

/**
 * Writes metrics to sessionStorage with a TTL expiry.
 * Silently ignores errors (e.g. private browsing, quota exceeded).
 */
export function setCachedMetrics(users: string, sessions: string): void {
  try {
    const entry: CachedMetrics = {
      users,
      sessions,
      expiry: Date.now() + METRICS_CACHE_TTL_MS
    };
    sessionStorage.setItem(METRICS_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Silently ignore storage errors
  }
}

// ── Formatting ─────────────────────────────────────────────────────────────────

/**
 * Formats a number with locale-appropriate thousand separators.
 * Exported for testing.
 */
export function formatMetricValue(n: number): string {
  return n.toLocaleString();
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Custom hook that provides Huddle+ usage statistics.
 *
 * Consumes UserMetrics context for user/session counts (filtering for 'huddle' applications),
 * and calls HuddleStatsService for meetings scheduled count.
 * Results are cached in sessionStorage with a 15-minute TTL.
 *
 * Returns { stats, isLoading } where stats includes users, teams, sessions, and meetings.
 */
export function useHuddleStats(): HuddleStats {
  const umCtx = useUserMetrics();
  const status = umCtx.finTechOpsUserMetricsDataStatus;
  const data = umCtx.userMetricsWithUserData?.data;

  const [meetingsScheduled, setMeetingsScheduled] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    HuddleStatsService.getMeetingsScheduledCount().then((count) => {
      if (!cancelled) setMeetingsScheduled(count);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { users, sessions, leadersSupported, metricsLoading } = useMemo(() => {
    const metricsReady = status === 'finished' && data?.length;
    const loading = !metricsReady && status === 'loading';
    const fallback = loading ? getCachedMetrics() : null;

    let computedUsers = fallback?.users ?? '—';
    let computedSessions = fallback?.sessions ?? '—';
    let computedLeaders = '—';

    if (metricsReady) {
      const huddleData = data.filter((item) => item.application?.toLowerCase().includes('huddle') && item.application !== ALL_APPLICATIONS);
      computedUsers = formatMetricValue(new Set(huddleData.map((item) => item.user_login)).size);
      computedSessions = formatMetricValue(huddleData.reduce((sum, item) => sum + (item.measure_value || 0), 0));

      // Count unique L8+ managers from user metrics hierarchy data
      const leaderLogins = new Set<string>();
      for (const item of huddleData) {
        const logins = (item as any).user_manager_login_hierarchy ?? [];
        const levels = (item as any).user_manager_job_level_hierarchy ?? [];
        for (let i = 0; i < logins.length; i++) {
          const level = parseInt(levels[i], 10);
          if (level >= 8 && logins[i]) leaderLogins.add(logins[i].toLowerCase());
        }
      }
      computedLeaders = formatMetricValue(leaderLogins.size);
    }

    return { users: computedUsers, sessions: computedSessions, leadersSupported: computedLeaders, metricsLoading: loading && !fallback };
  }, [status, data]);

  // Persist computed metrics to cache when valid
  useEffect(() => {
    if (users !== '—' && sessions !== '—') {
      setCachedMetrics(users, sessions);
    }
  }, [users, sessions]);

  return useMemo<HuddleStats>(() => {
    const stats: StatItem[] = [
      {
        id: 'users',
        icon: <img src={iconUsers} alt="Users" width={42} height={42} />,
        value: metricsLoading ? null : users,
        label: 'Users'
      },
      {
        id: 'leaders',
        icon: <img src={iconBuilding} alt="Leaders Supported" width={42} height={42} />,
        value: metricsLoading ? null : leadersSupported,
        label: 'Leaders Supported'
      },
      {
        id: 'sessions',
        icon: <img src={iconChart} alt="Sessions" width={42} height={33} />,
        value: metricsLoading ? null : sessions,
        label: 'Sessions'
      },
      {
        id: 'meetings',
        icon: <img src={iconCalendarHeart} alt="Meetings Scheduled" width={42} height={35} />,
        value: meetingsScheduled !== null ? formatMetricValue(meetingsScheduled) : null,
        label: 'Meetings Scheduled'
      }
    ];

    const isLoading = metricsLoading || meetingsScheduled === null;

    return { stats, isLoading };
  }, [users, sessions, leadersSupported, metricsLoading, meetingsScheduled]);
}

// ── StatsBar Component ─────────────────────────────────────────────────────────

interface StatsBarProps {
  stats?: StatItem[];
}

const StatsBar: React.FC<StatsBarProps> = ({ stats: statsProp }) => {
  const { stats: hookStats } = useHuddleStats();
  const stats = statsProp ?? hookStats;

  return (
    <div className={styles.statsContainer}>
      {stats.map((stat, index) => (
        <React.Fragment key={stat.id}>
          <div className={styles.stat}>
            <span className={styles.statIcon}>{stat.icon}</span>
            <div className={styles.statContent}>
              <span className={styles.statValue}>{stat.value !== null ? stat.value : <Spinner aria-label="Loading" />}</span>
              <span className={styles.statLabel}>{stat.label}</span>
            </div>
          </div>
          {index < stats.length - 1 && <div className={styles.statsDivider} />}
        </React.Fragment>
      ))}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════════════════════

interface AttendeePathPickerProps {
  onSelect: (path: AttendeePath) => void;
  onButtonClick?: (path: AttendeePath, buttonLabel: string) => void;
  disabledPaths?: Set<AttendeePath>;
  displayName: string;
  onFileDrop?: (file: File) => void;
  isProcessing?: boolean;
  processingError?: string | null;
}

export const AttendeePathPicker: React.FC<AttendeePathPickerProps> = ({
  onSelect,
  onButtonClick,
  disabledPaths,
  displayName,
  onFileDrop,
  isProcessing,
  processingError
}) => (
  <div className={styles.container}>
    {/* Soft ambient blurbs — purple/pink gradient orbs */}
    <div className={styles.orbsContainer} aria-hidden="true">
      <div className={styles.blurb} style={{ width: 262, height: 262, left: '40%', top: '5%', borderRadius: '131px' }} />
      <div className={styles.blurb} style={{ width: 229, height: 229, left: '55%', top: '25%' }} />
      <div className={styles.blurb} style={{ width: 196, height: 196, left: '60%', top: '8%', borderRadius: '98px' }} />
      <div className={styles.blurb} style={{ width: 164, height: 164, left: '45%', top: '18%', borderRadius: '82px' }} />
    </div>
    <div className={styles.content}>
      {/* Hero Section */}
      <div className={styles.hero}>
        <h1 className={styles.greeting}>
          Hey{' '}
          <span className={styles.gradientName} aria-label={displayName}>
            {displayName},
          </span>{' '}
          let{' '}
          <span className={styles.huddleBrand}>
            <img src={huddleLogo} alt="Huddle+" className={styles.logo} width={30} height={30} />
            huddle+
          </span>{' '}
          set up the meeting for you.
        </h1>
        <p className={styles.subtitle}>Stop playing email tag. Huddle+ finds the perfect time, and builds the invite for you effortlessly.</p>
      </div>

      {/* Action Cards */}
      <div className={styles.actionCardsSection}>
        <h2 className={styles.subheading}>How would you like to add attendees?</h2>
        <ActionCardsGrid
          onSelect={onSelect}
          onButtonClick={onButtonClick}
          disabledPaths={disabledPaths}
          onFileDrop={onFileDrop}
          isProcessing={isProcessing}
          processingError={processingError}
        />
      </div>

      {/* Stats */}
      <StatsBar />

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerContacts}>
          <span className={styles.footerContactsLabel}>CONTACT US</span>
          <div className={styles.footerContactsList}>
            <a className={styles.footerContactChip} href={HUDDLE_SLACK_CHANNEL.href} target="_blank" rel="noopener noreferrer">
              <img src={SLACK_ICON_URL} alt="" className={styles.footerContactAvatar} />
              <span className={styles.footerContactAlias}>{HUDDLE_SLACK_CHANNEL.name}</span>
            </a>
            {HUDDLE_CONTACTS.map((contact) => (
              <a
                key={contact.alias}
                className={styles.footerContactChip}
                href={`${SLACK_DM_URL}${contact.alias}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img className={styles.footerContactAvatar} src={generateProfilePictureUrl(contact.alias)} alt={contact.alias} />
                <span className={styles.footerContactAlias}>{contact.alias}@</span>
              </a>
            ))}
          </div>
        </div>
        <p className={styles.branding}>
          <span className={styles.brandName}># Huddle+</span>
          {' — Inspired by '}
          <span className={styles.brandHighlight}>EAs</span>
          {'. Designed in '}
          <span className={styles.brandHighlight}>Seattle</span>
          {'. Enhanced in '}
          <span className={styles.brandHighlight}>Delhi</span>
          {'.'}
        </p>
      </div>
    </div>
  </div>
);
